import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { resolveURLPath } from 'utils/urlHelpers';

import coreService from './core';
import uploadVideo from './upload';


async function activateFanclub({ username, price }) {
  const baseUrl = ConfigurationService.get(ConfigurationKeys.BASE_URL);
  const endPoint = `rest/v1.0/partner/fanclubs/${username}`;

  const url = new URL(baseUrl);
  url.pathname = resolveURLPath(url.pathname, endPoint);

  const payload = { amount: price };

  await ApiService.post(url.toString(), { data: payload });
}

export async function fetchSettings() {
  const userDetails = await coreService.getUser();
  if (!userDetails.isPerformer) {
    return { userDetails };
  }

  const url = coreService.computeURL(`models/${userDetails.id}/settings`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.get(url.toString(), { headers });

  return { ...data, userDetails };
}

export async function updatePrice({ price, username }) {
  const { id, isPerformer } = await coreService.getUser();
  const url = coreService.computeURL(`models/${id}/settings/fanclubPrice`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };
  const payload = { price };

  if (!isPerformer) {
    await activateFanclub({ username, price });
    await coreService.getToken({ forceRefresh: true });

    return {};
  }

  const { data } = await ApiService.put(url.toString(), { headers, data: payload });

  return data;
}

export async function uploadPromoVideo({ file, onProgress }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`models/${id}/settings/promoVideo`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  await uploadVideo({ url: url.toString(), headers, file, onProgress });
}

export async function uploadProfilePicture({ file, onProgress }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`models/${id}/settings/pictures/profile`);

  const { token } = await coreService.getToken();
  const headers = {
    'Authorization': `Bearer ${token}`,
    'content-type': 'multipart/form-data',
  };

  const formData = new FormData();
  formData.append('file', file);

  const data = await ApiService.put(url.toString(), {
    headers,
    data: formData,
    onUploadProgress: ({ loaded, total }) => {
      onProgress?.(Math.round(loaded / total * 100));
    },
  });

  return data;
}

export async function uploadBackgroundPicture({ file, onProgress }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`models/${id}/settings/pictures/promo`);

  const { token } = await coreService.getToken();
  const headers = {
    'Authorization': `Bearer ${token}`,
    'content-type': 'multipart/form-data',
  };

  const formData = new FormData();
  formData.append('file', file);

  const data = await ApiService.put(url.toString(), {
    headers,
    data: formData,
    onUploadProgress: ({ loaded, total }) => {
      onProgress?.(Math.round(loaded / total * 100));
    },
  });

  return data;
}

export async function updateBiography({ value }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`models/${id}/settings/bio`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const payload = { bio: value };

  const { data } = await ApiService.put(url.toString(), { headers, data: payload });

  return data;
}

export async function fetchMarketingGallery() {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`marketingGalleryForModels/${id}`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.get(url.toString(), { headers });

  return data;
}

export async function uploadMarketingImages({ images, onProgress }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`marketingGalleryForModels/${id}/images`);

  const { token } = await coreService.getToken();
  const headers = {
    'Authorization': `Bearer ${token}`,
    'content-type': 'multipart/form-data',
  };

  const formData = new FormData();
  for (const image of images) {
    formData.append('images', image);
  }

  const { data } = await ApiService.post(url.toString(), {
    headers,
    data: formData,
    onUploadProgress: ({ loaded, total }) => {
      onProgress?.(Math.round(loaded / total * 100));
    },
  });

  return data;
}

export async function deleteMarketingImage({ imageId }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`marketingGalleryForModels/${id}/images/${imageId}`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  await ApiService.delete(url.toString(), { headers });
}

export async function fetchMediaItems(options = {}) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`mediaItemsForModels/${id}`);

  const { page, size, sortBy, direction, mediaTypes, mediaAccessTypes, status } = options;

  url.searchParams.set('page', page || 0);
  url.searchParams.set('size', size || 10);

  if (mediaTypes) {
    url.searchParams.set('mediaTypes', mediaTypes);
  }

  if (mediaAccessTypes) {
    url.searchParams.set('mediaAccessTypes', mediaAccessTypes);
  }

  if (status) {
    url.searchParams.set('status', status);
  }

  if (sortBy) {
    url.searchParams.set('sortBy', sortBy);
  }

  if (direction) {
    url.searchParams.set('direction', direction);
  }

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.get(url.toString(), { headers });

  return data;
}

export async function fetchGalleryItems({ galleryId }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`mediaItemsForModels/${id}/galleriesWithImages/${galleryId}`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.get(url.toString(), { headers });

  return data;
}

export async function deleteMediaItem({ itemId, isVideo }) {
  const { id } = await coreService.getUser();
  let url = undefined;
  if (isVideo) {
    url = coreService.computeURL(`mediaItemsForModels/${id}/videos/${itemId}`);
  } else {
    url = coreService.computeURL(`mediaItemsForModels/${id}/galleries/${itemId}`);
  }

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.delete(url.toString(), { headers });

  return data;
}

export async function setVideoCover({ videoId, cover, onProgress }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`mediaItemsForModels/${id}/videos/${videoId}/cover`);

  const { token } = await coreService.getToken();
  const headers = {
    'Authorization': `Bearer ${token}`,
    'content-type': 'multipart/form-data',
  };

  const formData = new FormData();
  formData.append('cover', cover);

  const { data } = await ApiService.put(url.toString(), {
    headers,
    data: formData,
    onUploadProgress: ({ loaded, total }) => {
      onProgress?.(Math.round(loaded / total * 100));
    },
  });

  return data;
}

export async function uploadGallery({ images, name, isPrivate, price, scheduledAt, onProgress }) {
  const { id } = await coreService.getUser();
  let url = undefined;
  if (isPrivate) {
    url = coreService.computeURL(`mediaItemsForModels/${id}/privateGalleries`);
  } else {
    url = coreService.computeURL(`mediaItemsForModels/${id}/freeForFansGalleries`);
  }

  const { token } = await coreService.getToken();
  const headers = {
    'Authorization': `Bearer ${token}`,
    'content-type': 'multipart/form-data',
  };

  const formData = new FormData();
  formData.append('name', name);
  if (price) {
    formData.append('price', price);
  }
  if (scheduledAt) {
    formData.append('scheduledAt', scheduledAt);
  }
  for (const image of images) {
    formData.append('images', image);
  }

  const { data } = await ApiService.post(url.toString(), {
    headers,
    data: formData,
    onUploadProgress: ({ loaded, total }) => {
      onProgress?.(Math.round(loaded / total * 100));
    },
  });

  return data;
}

export async function uploadModelVideo({ file, title, isPrivate, price, scheduledAt, onProgress }) {
  const { id } = await coreService.getUser();
  let url = undefined;
  if (isPrivate) {
    url = coreService.computeURL(`mediaItemsForModels/${id}/uploadVideos/privateVideosRequests`);
  } else {
    url = coreService.computeURL(`mediaItemsForModels/${id}/uploadVideos/freeForFansVideosRequests`);
  }

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  await uploadVideo({ url: url.toString(), headers, file, title, price, scheduledAt, onProgress });
}

export async function updateItemTitle({ itemId, newTitle, isVideo }) {
  const { id } = await coreService.getUser();
  let url = undefined;
  if (isVideo) {
    url = coreService.computeURL(`mediaItemsForModels/${id}/videos/${itemId}/title`);
  } else {
    url = coreService.computeURL(`mediaItemsForModels/${id}/galleries/${itemId}/title`);
  }

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const payload = { newTitle };

  const { data } = await ApiService.put(url.toString(), { headers, data: payload });

  return data;
}

export async function updateItemFreeForFansStatus({ itemId, freeForFans, isVideo }) {
  const { id } = await coreService.getUser();
  let url = undefined;
  if (isVideo) {
    url = coreService.computeURL(`mediaItemsForModels/${id}/videos/${itemId}/freeForFansStatus`);
  } else {
    url = coreService.computeURL(`mediaItemsForModels/${id}/galleries/${itemId}/freeForFansStatus`);
  }

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const payload = { freeForFans };

  const { data } = await ApiService.put(url.toString(), { headers, data: payload });

  return data;
}

export async function updateItemPrice({ itemId, price, isVideo }) {
  const { id } = await coreService.getUser();
  let url = undefined;
  if (isVideo) {
    url = coreService.computeURL(`mediaItemsForModels/${id}/privateVideos/${itemId}/price`);
  } else {
    url = coreService.computeURL(`mediaItemsForModels/${id}/privateGalleries/${itemId}/price`);
  }

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const payload = { price };

  const { data } = await ApiService.patch(url.toString(), { headers, data: payload });

  return data;
}

export async function updateItemDuration({ itemId, publicDurationInDays, isVideo }) {
  const { id } = await coreService.getUser();
  let url = undefined;
  if (isVideo) {
    url = coreService.computeURL(`mediaItemsForModels/${id}/privateVideos/${itemId}/publicDuration`);
  } else {
    url = coreService.computeURL(`mediaItemsForModels/${id}/privateGalleries/${itemId}/publicDuration`);
  }

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const payload = { publicDurationInDays };

  const { data } = await ApiService.patch(url.toString(), { headers, data: payload });

  return data;
}

export async function updateItemScheduleDate({ itemId, scheduledAt, isVideo }) {
  const { id } = await coreService.getUser();
  let url = undefined;
  if (isVideo) {
    url = coreService.computeURL(`mediaItemsForModels/${id}/videos/${itemId}/scheduledAt`);
  } else {
    url = coreService.computeURL(`mediaItemsForModels/${id}/galleries/${itemId}/scheduledAt`);
  }

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const payload = { scheduledAt };

  const { data } = await ApiService.put(url.toString(), { headers, data: payload });

  return data;
}


export async function updateGalleryCover({ galleryId, imageId }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`mediaItemsForModels/${id}/galleries/${galleryId}/cover`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const payload = { imageId };

  const { data } = await ApiService.put(url.toString(), { headers, data: payload });

  return data;
}

export async function deleteGalleryImage({ galleryId, imageId }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`mediaItemsForModels/${id}/galleries/${galleryId}/images/${imageId}`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.delete(url.toString(), { headers });

  return data;
}

export async function uploadImagesToGallery({ galleryId, images, onProgress }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`mediaItemsForModels/${id}/galleries/${galleryId}/images`);

  const { token } = await coreService.getToken();
  const headers = {
    'Authorization': `Bearer ${token}`,
    'content-type': 'multipart/form-data',
  };

  const formData = new FormData();
  for (const image of images) {
    formData.append('images', image);
  }

  const { data } = await ApiService.post(url.toString(), {
    headers,
    data: formData,
    onUploadProgress: ({ loaded, total }) => {
      onProgress?.(Math.round(loaded / total * 100));
    },
  });

  return data;
}

export async function fetchAutoTweetsSettings() {
  const url = coreService.computeURL('autoTweetsSettings');

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.get(url.toString(), { headers });

  return data;
}

export async function deleteAutoTweetsSettings() {
  const url = coreService.computeURL('autoTweetsSettings');

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.delete(url.toString(), { headers });

  return data;
}

export async function fetchAutoTweetsGallery() {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`marketingGalleryForModels/${id}/availableForAutoTweetsImages`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.get(url.toString(), { headers });

  return data;
}

export async function updateAutoTweetsSettings({ settingId, text, imageId, imageType }) {
  const url = coreService.computeURL(`autoTweetsSettings/${settingId}`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const payload = { text, imageId, imageType };

  const { data } = await ApiService.put(url.toString(), { headers, data: payload });

  return data;
}

export async function deleteSingleAutoTweetsSetting({ settingId }) {
  const url = coreService.computeURL(`autoTweetsSettings/${settingId}`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const { data } = await ApiService.delete(url.toString(), { headers });

  return data;
}

export async function updateImagesOrder({ galleryId, images }) {
  const { id } = await coreService.getUser();
  const url = coreService.computeURL(`mediaItemsForModels/${id}/galleries/${galleryId}/images`);

  const { token } = await coreService.getToken();
  const headers = { 'Authorization': `Bearer ${token}` };

  const payload = { images };

  const { data } = await ApiService.patch(url.toString(), { headers, data: payload });

  return data;
}
